export interface RealtimeStreamReference {
  readonly type: string;
  readonly id: string;
  readonly sequence: number;
}

export interface RealtimeEventEnvelope {
  readonly eventId: string;
  readonly eventType: string;
  readonly eventVersion: number;
  readonly occurredAt: string;
  readonly correlationId: string;
  readonly stream: RealtimeStreamReference;
  readonly data: Readonly<Record<string, unknown>>;
}

export interface RealtimeSocket {
  onclose: ((event: CloseEvent) => void) | null;
  onmessage: ((event: MessageEvent<unknown>) => void) | null;
  onopen: ((event: Event) => void) | null;
  close(code?: number, reason?: string): void;
  send(value: string): void;
}

export type RealtimeConnectionState =
  | "connecting"
  | "connected"
  | "synchronizing"
  | "disconnected";

export interface RealtimeClientOptions {
  readonly channel?: string;
  readonly clientInstanceId: string;
  readonly initialSequence: number;
  readonly issueTicket: () => Promise<string>;
  readonly onConnectionState?: (state: RealtimeConnectionState) => void;
  readonly onError?: (code: string) => void;
  readonly onEvent: (event: RealtimeEventEnvelope) => Promise<number>;
  readonly onResync: (recoveryUrl: string | null, currentSequence?: number) => Promise<number>;
  readonly requestIdPrefix?: string;
  readonly resourceId?: string;
  readonly sessionId: string;
  readonly socketFactory?: (url: string) => RealtimeSocket;
  readonly streamType?: string;
  readonly url: URL;
}

const RECONNECT_DELAY_MS = 1_000;
const EVENT_ID_WINDOW_SIZE = 256;

function record(value: unknown): Readonly<Record<string, unknown>> | null {
  return typeof value === "object" && value !== null
    ? value as Readonly<Record<string, unknown>>
    : null;
}

function parseEvent(
  value: Readonly<Record<string, unknown>>,
  expectedStreamType: string,
): RealtimeEventEnvelope | null {
  const stream = record(value.stream);
  const data = record(value.data);
  if (
    typeof value.eventId !== "string"
    || typeof value.eventType !== "string"
    || typeof value.eventVersion !== "number"
    || !Number.isSafeInteger(value.eventVersion)
    || typeof value.occurredAt !== "string"
    || typeof value.correlationId !== "string"
    || stream?.type !== expectedStreamType
    || typeof stream.id !== "string"
    || typeof stream.sequence !== "number"
    || !Number.isSafeInteger(stream.sequence)
    || stream.sequence < 1
    || data === null
  ) {
    return null;
  }
  return Object.freeze({
    correlationId: value.correlationId,
    data,
    eventId: value.eventId,
    eventType: value.eventType,
    eventVersion: value.eventVersion,
    occurredAt: value.occurredAt,
    stream: Object.freeze({
      id: stream.id,
      sequence: stream.sequence,
      type: expectedStreamType,
    }),
  });
}

export class RealtimeClient {
  readonly #channel: string;
  readonly #clientInstanceId: string;
  readonly #issueTicket: () => Promise<string>;
  readonly #onConnectionState: (state: RealtimeConnectionState) => void;
  readonly #onError: (code: string) => void;
  readonly #onEvent: (event: RealtimeEventEnvelope) => Promise<number>;
  readonly #onResync: (recoveryUrl: string | null, currentSequence?: number) => Promise<number>;
  readonly #requestIdPrefix: string;
  readonly #resourceId: string;
  readonly #socketFactory: (url: string) => RealtimeSocket;
  readonly #streamType: string;
  readonly #url: URL;
  readonly #seenEventIds = new Set<string>();
  readonly #seenEventOrder: string[] = [];
  readonly #pendingEventIds = new Set<string>();
  #eventChain: Promise<void> = Promise.resolve();
  #eventEpoch = 0;
  #lastSequence: number;
  #needsReconnectResync = false;
  #reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  #requestSequence = 0;
  #socket: RealtimeSocket | undefined;
  #stopped = true;
  #suspended = false;

  constructor(options: RealtimeClientOptions) {
    if (!Number.isSafeInteger(options.initialSequence) || options.initialSequence < 0) {
      throw new TypeError("Realtime initial sequence must be a non-negative safe integer");
    }
    this.#channel = options.channel ?? "support_session";
    this.#clientInstanceId = options.clientInstanceId;
    this.#issueTicket = options.issueTicket;
    this.#lastSequence = options.initialSequence;
    this.#onConnectionState = options.onConnectionState ?? (() => undefined);
    this.#onError = options.onError ?? (() => undefined);
    this.#onEvent = options.onEvent;
    this.#onResync = options.onResync;
    this.#requestIdPrefix = options.requestIdPrefix ?? this.#channel.replace(/_/g, "-");
    this.#resourceId = options.resourceId ?? options.sessionId;
    this.#socketFactory = options.socketFactory ?? ((url) => new WebSocket(url));
    this.#streamType = options.streamType ?? this.#channel;
    this.#url = new URL(options.url);
  }

  get clientInstanceId(): string {
    return this.#clientInstanceId;
  }

  get lastSequence(): number {
    return this.#lastSequence;
  }

  async connect(): Promise<void> {
    if (!this.#stopped && this.#socket !== undefined) return;
    this.#stopped = false;
    await this.#openSocket();
  }

  close(): void {
    this.#stopped = true;
    this.#suspended = true;
    if (this.#reconnectTimer !== undefined) clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = undefined;
    const socket = this.#socket;
    this.#socket = undefined;
    if (socket !== undefined) {
      socket.onclose = null;
      socket.onmessage = null;
      socket.onopen = null;
      socket.close();
    }
    this.#onConnectionState("disconnected");
  }

  suspend(): void {
    if (this.#stopped) return;
    this.#eventEpoch += 1;
    this.#suspended = true;
  }

  resynchronize(recoveryUrl: string | null = null): Promise<void> {
    this.#eventChain = this.#eventChain.then(() => this.#resync(recoveryUrl));
    return this.#eventChain;
  }

  async #openSocket(): Promise<void> {
    if (this.#stopped) return;
    this.#onConnectionState("connecting");
    try {
      const ticket = await this.#issueTicket();
      if (this.#stopped) return;
      const socket = this.#socketFactory(this.#url.toString());
      this.#socket = socket;
      socket.onopen = () => {
        if (this.#socket !== socket || this.#stopped) return;
        socket.send(JSON.stringify({ ticket, type: "authenticate", version: 1 }));
      };
      socket.onmessage = (event) => this.#handleMessage(socket, event);
      socket.onclose = () => {
        if (this.#socket !== socket) return;
        this.#socket = undefined;
        if (this.#stopped) return;
        this.#needsReconnectResync = true;
        this.#onConnectionState("disconnected");
        this.#scheduleReconnect();
      };
    } catch {
      if (this.#stopped) return;
      this.#onError("realtime_ticket_failed");
      this.#onConnectionState("disconnected");
      this.#scheduleReconnect();
    }
  }

  #handleMessage(socket: RealtimeSocket, event: MessageEvent<unknown>): void {
    if (this.#socket !== socket || this.#stopped || typeof event.data !== "string") return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(event.data) as unknown;
    } catch {
      this.#onError("invalid_frame");
      return;
    }
    const message = record(parsed);
    if (message === null) return;
    if (message.type === "authenticated") {
      this.#onConnectionState("connected");
      if (this.#needsReconnectResync) {
        this.#needsReconnectResync = false;
        void this.resynchronize();
      } else {
        this.#subscribe();
      }
      return;
    }
    if (message.type === "error") {
      if (message.code === "resync_required") {
        const recoveryUrl = typeof message.recoveryUrl === "string" ? message.recoveryUrl : null;
        const currentSequence = typeof message.currentSequence === "number"
          && Number.isSafeInteger(message.currentSequence)
          && message.currentSequence >= 0
          ? message.currentSequence
          : undefined;
        this.#eventChain = this.#eventChain.then(() => this.#resync(recoveryUrl, currentSequence));
      } else if (typeof message.code === "string" && message.code !== "backpressure_warning") {
        this.#onError(message.code);
      }
      return;
    }
    if (this.#suspended) return;
    const envelope = parseEvent(message, this.#streamType);
    if (envelope === null || envelope.stream.id !== this.#resourceId) return;
    if (
      this.#seenEventIds.has(envelope.eventId)
      || this.#pendingEventIds.has(envelope.eventId)
    ) return;
    this.#pendingEventIds.add(envelope.eventId);
    const eventEpoch = this.#eventEpoch;
    this.#eventChain = this.#eventChain.then(async () => {
      try {
        if (eventEpoch !== this.#eventEpoch) return;
        await this.#applyEvent(envelope);
      } finally {
        this.#pendingEventIds.delete(envelope.eventId);
      }
    });
  }

  async #applyEvent(event: RealtimeEventEnvelope): Promise<void> {
    if (this.#stopped || this.#suspended) return;
    if (event.stream.sequence !== this.#lastSequence + 1) {
      await this.#resync(null, event.stream.sequence);
      return;
    }
    this.#rememberEvent(event.eventId);
    try {
      const authoritativeSequence = await this.#onEvent(event);
      if (
        !Number.isSafeInteger(authoritativeSequence)
        || authoritativeSequence < event.stream.sequence
      ) {
        await this.#resync(null);
        return;
      }
      this.#lastSequence = authoritativeSequence;
    } catch {
      await this.#resync(null);
    }
  }

  async #resync(recoveryUrl: string | null, currentSequence?: number): Promise<void> {
    if (this.#stopped) return;
    this.#eventEpoch += 1;
    this.#suspended = true;
    this.#onConnectionState("synchronizing");
    try {
      const authoritativeSequence = await this.#onResync(recoveryUrl, currentSequence);
      if (this.#stopped) return;
      if (!Number.isSafeInteger(authoritativeSequence) || authoritativeSequence < 0) {
        throw new TypeError("Realtime resync returned an invalid sequence");
      }
      this.#lastSequence = authoritativeSequence;
      this.#subscribe();
      this.#onConnectionState("connected");
    } catch {
      this.#onError("realtime_resync_failed");
      this.#socket?.close();
    } finally {
      this.#suspended = false;
    }
  }

  #subscribe(): void {
    if (this.#stopped || this.#socket === undefined) return;
    this.#requestSequence += 1;
    this.#socket.send(JSON.stringify({
      afterSequence: this.#lastSequence,
      channel: this.#channel,
      requestId: `${this.#requestIdPrefix}-${this.#requestSequence}`,
      resourceId: this.#resourceId,
      type: "subscribe",
      version: 1,
    }));
  }

  #rememberEvent(eventId: string): void {
    this.#seenEventIds.add(eventId);
    this.#seenEventOrder.push(eventId);
    if (this.#seenEventOrder.length <= EVENT_ID_WINDOW_SIZE) return;
    const expired = this.#seenEventOrder.shift();
    if (expired !== undefined) this.#seenEventIds.delete(expired);
  }

  #scheduleReconnect(): void {
    if (this.#stopped || this.#reconnectTimer !== undefined) return;
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = undefined;
      void this.#openSocket();
    }, RECONNECT_DELAY_MS);
  }
}
